<template>
  <div class="popUpWrap">
    <section class="popup light">
      <div class="topArea">
        <h4>{{ t("profile-education") }}</h4>
        <div class="exit" @click.stop="onClickClose"></div>
      </div>
      <div class="scrollY">
        <ul class="infoList flex">
          <li class="flex">
            <div class="tit">{{ t("profile-education-school-name") }}</div>
            <input
              id="educationSchoolInput"
              v-model="educationData.schoolName"
              type="text"
              :class="{error : !isValidStringSchoolName}"
              :placeholder="t('profile-education-school-name-placeholder')">
            <p v-if="!isValidStringSchoolName" class="errorMessage">
              <span></span>
              {{ t("profile-education-school-name-error") }}
            </p>
          </li>
          <li class="flex">
            <div class="tit">{{ t("profile-education-degree") }} <span>{{ t("common-optional", 0) }}</span>
            </div>
            <input
              id="educationDegreeInput"
              v-model="educationData.degree"
              type="text"
              :class="{error : !isValidStringDegree}"
              :placeholder="t('profile-education-degree-placeholder')">
            <p v-if="!isValidStringDegree" class="errorMessage">
              <span></span>
              {{ t("profile-education-degree-error") }}
            </p>
          </li>
          <li class="flex">
            <div class="tit">{{ t("profile-education-major") }} <span>{{ t("common-optional", 0) }}</span>
            </div>
            <input
              id="educationMajorInput"
              v-model="educationData.major"
              type="text"
              :class="{error : !isValidStringMajor}"
              :placeholder="t('profile-education-major-placeholder')">
            <p v-if="!isValidStringMajor" class="errorMessage">
              <span></span>
              {{ t("profile-education-major-error") }}
            </p>
          </li>
          <li class="flex">
            <ul class="checkBoxList">
              <li>
                <input id="item1" v-model="educationData.currentlyStudyingFlag" type="checkbox">
                <label for="item1">{{ t("profile-education-current-studying") }}</label>
              </li>
            </ul>
            <ul v-if="isLoading" class="dateArea flex">
              <InformationPeriodSelect
                :title="t('common-start-date')"
                :default-button-string-month="t('common-month')"
                :default-button-string-year="t('common-year')"
                :default-date="educationData.startDate"
                :is-month-open-flag="periodMenuFlag.startMonth"
                :is-year-open-flag="periodMenuFlag.startYear"
                :is-bottom-up-open="true"
                @onChangeDate="onChangeStartData"
                @onChangeMenuFlag="onChangeStartMenuFlag"
              />
              <InformationPeriodSelect
                v-if="!educationData.currentlyStudyingFlag"
                :title="t('common-end-date')"
                :default-button-string-month="t('common-month')"
                :default-button-string-year="t('common-year')"
                :default-date="educationData.endDate"
                :is-month-open-flag="periodMenuFlag.endMonth"
                :is-year-open-flag="periodMenuFlag.endYear"
                :is-bottom-up-open="true"
                :is-error="!isValidPeriod"
                @onChangeDate="onChangeEndData"
                @onChangeMenuFlag="onChangeEndMenuFlag"
              />
            </ul>
            <p v-if="!isValidPeriod" class="errorMessage"><span></span>{{ t("profile-period-error") }}</p>
          </li>
        </ul>
        <div class="btnArea">
          <button
            v-if="educationData.id !== 0"
            id="educationDeleteBtn"
            class="btn cancel"
            @click.stop="onClickOpenDeletePopup"
          >
            <span></span>
            {{ t("common-delete") }}
          </button>
          <button
            class="btn"
            :class="{ disabled: !isValidDisableButton }"
            :disabled="!isValidDisableButton"
            @click.stop="onClickSave"
          >
            {{ t("common-save") }}
          </button>
        </div>
      </div>
    </section>
    <div v-if="isOpenDeletePopup" class="popUpWrap">
      <section class="popup dark basic">
        <div class="topArea">
          <div id="educationRemovePopupExitBtn" class="exit" @click.stop="isOpenDeletePopup = false"></div>
        </div>
        <div class="scrollY">
          <h5>{{ t('profile-education-delete-title') }}</h5>
          <div class="btnArea">
            <button class="cancel" @click.stop="isOpenDeletePopup = false">{{ t("common-cancel") }}</button>
            <button @click.stop="onClickDelete">{{ t("common-delete") }}</button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useDialogStore } from "~/store/dialog/dialogStore";
import { onMounted } from "#imports";
import { useToastStore } from "~/store/toast/toastStore";
import { useProfileService } from "~~/application/composables/service/useProfileService";
import InformationPeriodSelect
  from "~~/application/components/Common/Dialog/dialog/Profile/InformationPeriodSelect.vue";
import {
  ProfileEducationCreateModel,
  ProfileUserInformationEducationModel
} from "~~/package/mapper/profile/model/ProfileUserInformationEducationModel";
import { ToastType } from "~/components/Common/Toast/model/ToastType";

const { t } = useI18n();
const { $i18n } = useNuxtApp();
const lang = computed(() =>
  $i18n.locale.value.toUpperCase()
);

const dialogStore = useDialogStore();
const toastStore = useToastStore();
const profileService = useProfileService();
const educationData = ref(new ProfileUserInformationEducationModel());
const isLoading = ref(false);
const isOpenDeletePopup = ref(false);

const periodMenuFlag = ref({
  startMonth: false,
  startYear: false,
  endMonth: false,
  endYear: false
})

onMounted(() => {
  educationData.value = { ...dialogStore.options.props.education };
  isLoading.value = true;
});

const isValidStringSchoolName = computed(() => {
  return educationData.value.schoolName.length <= 50;
});

const isValidStringDegree = computed(() => {
  return educationData.value.degree.length <= 50;
});

const isValidStringMajor = computed(() => {
  return educationData.value.major.length <= 50;
});

const isValidPeriod = computed(() => {
  if(!educationData.value.startDate || !educationData.value.endDate || educationData.value.currentlyStudyingFlag)
    return true;
  else {
    return new Date(educationData.value.endDate).getTime() >= new Date(educationData.value.startDate).getTime()
  }
})

const isValidDisableButton = computed(() => {
  return educationData.value.schoolName.length > 0 &&
    educationData.value.startDate?.length > 0 &&
    (educationData.value.currentlyStudyingFlag || educationData.value.endDate?.length > 0) &&
    isValidStringSchoolName.value &&
    isValidStringDegree.value &&
    isValidStringMajor.value &&
    isValidPeriod.value
});

const onChangeStartData = (data: string) => {
  educationData.value.startDate = data
}

const onChangeEndData = (data: string) => {
  educationData.value.endDate = data
}

const onClickSave = () => {
  const editEducation: ProfileEducationCreateModel = {
    id: educationData.value.id,
    schoolName: educationData.value.schoolName,
    degree: educationData.value.degree,
    major: educationData.value.major,
    startDate: educationData.value.startDate,
    endDate: educationData.value.endDate,
    currentlyStudyingFlag: educationData.value.currentlyStudyingFlag
  }

  if (educationData.value.id === 0) {
    profileService.addEducation(editEducation).then(response => {
      if(response){
        toastStore.openToast({
          toastType: ToastType.SUCCESS,
          contents: t(`profile-change-success`)
        })
        dialogStore.options.confirm?.event();
        onClickClose();
      }
      else {
        toastStore.openToast({
          toastType: ToastType.FAILED,
          contents: t(`profile-change-fail`)
        })
      }
    }).catch(() => {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t(`profile-change-fail`)
      })
    })
  }
  else {
    profileService.modifyEducation(editEducation).then(response => {
      if(response){
        toastStore.openToast({
          toastType: ToastType.SUCCESS,
          contents: t(`profile-change-success`)
        })
        dialogStore.options.confirm?.event();
        onClickClose();
      }
      else {
        toastStore.openToast({
          toastType: ToastType.FAILED,
          contents: t(`toast-message.fail-change`)
        })
      }
    }).catch(() => {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t(`toast-message.fail-change`)
      })
    })
  }
};

const onChangeStartMenuFlag = (menu: string, flag: boolean) => {
  if(menu === "month"){
    periodMenuFlag.value.startMonth = flag
    periodMenuFlag.value.startYear = false;
    periodMenuFlag.value.endMonth = false;
    periodMenuFlag.value.endYear = false;
  }else if(menu === "year"){
    periodMenuFlag.value.startMonth = false
    periodMenuFlag.value.startYear = flag;
    periodMenuFlag.value.endMonth = false;
    periodMenuFlag.value.endYear = false;
  }
}

const onChangeEndMenuFlag = (menu: string, flag: boolean) => {
  if(menu === "month"){
    periodMenuFlag.value.startMonth = false;
    periodMenuFlag.value.startYear = false;
    periodMenuFlag.value.endMonth = flag;
    periodMenuFlag.value.endYear = false;
  }else if(menu === "year"){
    periodMenuFlag.value.startMonth = false
    periodMenuFlag.value.startYear = false;
    periodMenuFlag.value.endMonth = false;
    periodMenuFlag.value.endYear = flag;
  }
}

const onClickOpenDeletePopup = () => {
  isOpenDeletePopup.value = true;
};

const onClickDelete = () => {
  isOpenDeletePopup.value = false;

  profileService.deleteEducation(educationData.value.id).then(response => {
    if (response) {
      toastStore.openToast({
        toastType: ToastType.SUCCESS,
        contents: t(`profile-education-delete-success`)
      });

      dialogStore.options.confirm?.event();
      dialogStore.close();
    } else {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t(`profile-education-delete-fail`)
      });
    }
  }).catch(() => {
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: t(`profile-education-delete-fail`)
    });
  });
};

const onClickClose = () => {
  dialogStore.close();
};
</script>

